import React, { useState } from 'react';

const CreateTaskModal = ({
    isOpen,
    onClose,
    onCreate,
    projectName,
    isCreating,
    members,
    lead,
    isAdmin = false,
    isTemplateMode = false
}) => {
    const [taskForm, setTaskForm] = useState({
        subject: '',
        description: '',
        assignedUsers: []
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!taskForm.subject.trim()) return;

        try {
            // Build assignedUsersNames from the selected users
            const assignedUsersNames = taskForm.assignedUsers.map(email => {
                const member = (members || []).find(m => String(m.email) === String(email));
                return member?.name || email;
            });
            const assignedToName = assignedUsersNames[0] || null;

            await onCreate({
                ...taskForm,
                assignedUsersNames,
                assignedToName
            });
            // Reset form on success
            setTaskForm({ subject: '', description: '', assignedUsers: [] });
            onClose();
        } catch (err) {
            console.error('Task creation failed:', err);
        }
    };

    const handleClose = () => {
        setTaskForm({ subject: '', description: '', assignedUsers: [] });
        onClose();
    };

    const toggleAssignee = (email) => {
        setTaskForm(prev => ({
            ...prev,
            assignedUsers: prev.assignedUsers.includes(email)
            ? prev.assignedUsers.filter(e => e !== email)
            : [...prev.assignedUsers, email]
        }));
    };

    if (!isOpen) return null;

    // Only show team members (users with clientRole)
    const teamMembers = members || [];

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b sticky top-0 bg-white z-10">
        <div className="flex justify-between items-center">
        <div>
        <h2 className="text-xl font-semibold text-gray-900">Create New Task</h2>
        <p className="text-sm text-gray-600 mt-1">
        Creating task in <span className="font-medium">{projectName}</span>{!isAdmin && ' Team swimlane'}
        </p>
        </div>
        <button
        onClick={handleClose}
        className="text-gray-400 hover:text-gray-600"
        disabled={isCreating}
        >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
        </button>
        </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {/* Task Subject */}
        <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
        Task Title *
        </label>
        <input
        type="text"
        value={taskForm.subject}
        onChange={(e) => setTaskForm({ ...taskForm, subject: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        placeholder="Enter task title"
        disabled={isCreating}
        required
        />
        </div>

        {/* Task Description */}
        <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
        Description
        </label>
        <textarea
        value={taskForm.description}
        onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
        rows="4"
        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
        placeholder="Enter task description (optional)"
        disabled={isCreating}
        />
        </div>

        {/* Assign Team Members - hide in template mode */}
        {!isTemplateMode && (
        <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
        Assign Team Members
        </label>
        <div className="border border-gray-300 rounded p-3 max-h-48 overflow-y-auto space-y-2">
        {teamMembers.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No team members available</p>
        ) : (
            teamMembers.map(member => (
                <label
                key={member.leadId}
                className={`flex items-center gap-3 p-2 rounded ${member.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`}
                title={member.disabled ? 'User is disabled' : undefined}
                >
                <input
                type="checkbox"
                checked={taskForm.assignedUsers.some(id => String(id) === String(member.email))}
                onChange={() => toggleAssignee(member.email)}
                disabled={isCreating || member.disabled === true}
                className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                />
                <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                {member.name || member.email}
                </p>
                <p className="text-xs text-gray-500">{member.email}</p>
                </div>
                </label>
            ))
        )}
        </div>
        {taskForm.assignedUsers.length > 0 && (
            <p className="text-xs text-gray-600 mt-2">
            {taskForm.assignedUsers.length} member(s) selected
            </p>
        )}
        </div>
        )}

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
        {!isAdmin && (
            <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-blue-700">
            Tasks are created in {projectName ? `${projectName}'s Team` : 'the Team'} swimlane and will be visible to all team members.
            </p>
            </div>
        )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
        <button
        type="button"
        onClick={handleClose}
        disabled={isCreating}
        className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
        >
        Cancel
        </button>
        <button
        type="submit"
        disabled={!taskForm.subject.trim() || isCreating}
        className="btn-primary flex items-center gap-2"
        >
        {isCreating ? (
            <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Creating...
            </>
        ) : (
            <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Task
            </>
        )}
        </button>
        </div>
        </form>
        </div>
        </div>
    );
};

export default CreateTaskModal;
